import { type ReactElement, useMemo } from 'react';
import { useLocation } from 'react-router';
import { useConfig } from '@salesforce/storefront-next-runtime/config';
import { useAuth } from '@/providers/auth';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { cn } from '@/lib/utils';
import { SvgUserAvatar } from 'src/components/icons-eataly';
import { EatalyHeaderAccountMenu } from './eataly-header-account-menu';
import { Link } from 'src/components/link';
import type { AppConfig } from 'src/types/config';
import { useCommerceEnabled } from '@/hooks/use-commerce-enabled';
import { buildLoginHrefFromLocation } from '@/components/login/login-auth-shared';

export type EatalyUserActionsVariant = 'header' | 'menu';

export type EatalyUserActionsProps = {
    variant?: EatalyUserActionsVariant;
    onClose?: () => void;
};

function getInitial(value?: string): string {
    const v = (value ?? '').trim();
    return v ? v.charAt(0).toUpperCase() : '';
}

function getInitials(firstName?: string, lastName?: string): string {
    const first = getInitial(firstName);
    const last = getInitial(lastName);
    if (first && last) return `${first}${last}`;
    const one = first || last;
    const fromSingle = (firstName ?? lastName ?? '').trim().slice(0, 2).toUpperCase();
    return one || fromSingle || '';
}

const EatalyUserAvatarInitials = ({ initials }: { initials: string }) => {
    return (
        <span
            aria-hidden
            className={cn(
                'font-semibold uppercase',
                'text-[length:var(--font-size-50)] leading-[var(--line-height-50)] tracking-[var(--tracking-label-s)]'
            )}>
            {initials}
        </span>
    );
};

const EatalyUserAvatarMenuContent = ({ isAuthenticated, initials }: { isAuthenticated: boolean; initials: string }) => {
    if (!isAuthenticated || !initials) {
        return <SvgUserAvatar className="size-6 text-foreground" aria-hidden />;
    }

    return (
        <span
            aria-hidden
            className="flex size-full items-center justify-center rounded-full bg-primary text-primary-foreground">
            <EatalyUserAvatarInitials initials={initials} />
        </span>
    );
};

const EatalyUserAvatarHeaderContent = ({
    isAuthenticated,
    initials,
}: {
    isAuthenticated: boolean;
    initials: string;
}) => {
    if (!isAuthenticated) {
        return <SvgUserAvatar className="size-6" aria-hidden />;
    }
    if (initials) {
        return <EatalyUserAvatarInitials initials={initials} />;
    }
    return <SvgUserAvatar className="size-6" aria-hidden />;
};

export default function EatalyUserActions({
    variant = 'header',
    onClose,
}: EatalyUserActionsProps): ReactElement | null {
    const session = useAuth();
    const location = useLocation();
    const config = useConfig<AppConfig>();
    const commerceEnabled = useCommerceEnabled();
    const loginHref = buildLoginHrefFromLocation(location.pathname, location.search);
    const { t } = useTranslation('header');
    const { t: tAccount } = useTranslation('account');
    const isAuthenticated: boolean = useMemo(() => {
        return Boolean(session?.userType === 'registered' && session?.customerId);
    }, [session]);
    const isSocialLoginAvailable = Boolean(
        config.features.socialLogin?.enabled && config.features.socialLogin.providers.length > 0
    );
    const opensPopover = isAuthenticated || isSocialLoginAvailable;

    const ariaLabel = isAuthenticated ? tAccount('myAccount') : t('signIn');
    const initials = useMemo(
        () => getInitials(session?.firstName, session?.lastName),
        [session?.firstName, session?.lastName]
    );

    const icon =
        variant === 'menu' ? (
            <EatalyUserAvatarMenuContent isAuthenticated={isAuthenticated} initials={initials} />
        ) : (
            <EatalyUserAvatarHeaderContent isAuthenticated={isAuthenticated} initials={initials} />
        );

    if (!commerceEnabled) {
        return null;
    }

    if (variant === 'menu') {
        const menuDestination = isAuthenticated ? '/account' : loginHref;

        return (
            <div className="shrink-0 rounded-full bg-sand-95">
                <Button
                    className="inline-flex size-11 items-center justify-center rounded-full bg-sand-95 p-0 text-foreground transition-colors hover:bg-muted/80"
                    aria-label={ariaLabel}
                    hierarchy="primary"
                    iconOnly={true}
                    size="sm"
                    asChild>
                    <Link
                        to={menuDestination}
                        onClick={onClose}
                        {...(!isAuthenticated
                            ? {
                                  'data-analytics-event': 'nav_cta_click',
                                  'data-analytics-payload': JSON.stringify({
                                      click_text: ariaLabel,
                                      click_location: 'navbar',
                                  }),
                              }
                            : {})}>
                        {icon}
                    </Link>
                </Button>
            </div>
        );
    }

    const trigger = opensPopover ? (
        <Button
            className="cursor-pointer size-6"
            aria-label={ariaLabel}
            hierarchy={isAuthenticated ? 'primary' : 'tertiary'}
            iconOnly={true}>
            {icon}
        </Button>
    ) : (
        <Button className="cursor-pointer" aria-label={ariaLabel} hierarchy="tertiary" iconOnly={true} asChild>
            <Link
                to={loginHref}
                data-analytics-event="nav_cta_click"
                data-analytics-payload={JSON.stringify({
                    click_text: ariaLabel,
                    click_location: 'navbar',
                })}>
                {icon}
            </Link>
        </Button>
    );

    return <EatalyHeaderAccountMenu isAuthenticated={isAuthenticated} trigger={trigger} />;
}
